import { getRouter } from '@/router/holder'

/**
 * @param {object} obj
 * @returns {boolean}
 */
function looksLikeDelegatedDeletionCiJobPayload (obj) {
  return obj
    && typeof obj === 'object'
    && obj.id !== undefined
    && obj.id !== null
    && (
      (typeof obj.job_name === 'string' && typeof obj.pipeline_name === 'string')
      || (typeof obj.api_url === 'string' && obj.api_url.includes('/builds/'))
    )
}

/**
 * Unwrap Swagger `{ data: job | { jobs } }` envelopes (possibly nested). Needed when delegation flags sit
 * beside nested payloads (e.g. `pipeline_hook_triggered` + `{ data: { … } }`).
 *
 * @param {object} raw
 * @returns {object}
 */
function normalizeDelegationPayload (raw) {
  if (raw === null || raw === undefined || typeof raw !== 'object') return raw
  if (Array.isArray(raw)) return raw
  let cur = raw
  for (let depth = 0; depth < 4; depth++) {
    if (looksLikeDelegatedDeletionCiJobPayload(cur)) return cur
    if (Array.isArray(cur.jobs) && cur.jobs.length > 0 && cur.jobs.every(looksLikeDelegatedDeletionCiJobPayload)) return cur

    const inner = cur.data
    if (_.isPlainObject(inner) && !Array.isArray(inner)) {
      cur = { ..._.omit(cur, ['data']), ...inner }
      continue
    }
    break
  }
  return cur
}

/** Concourse job activity JSON often nests runnable builds here instead of a flat `jobs` array. */
const DELEGATION_NESTED_BUILD_KEYS = ['finished_build', 'next_build', 'current_build', 'previous_build']

/**
 * Turn API `data` into a flat list of build payloads we can link and label (supports multiple hooks / builds).
 *
 * @param {*} data
 * @returns {object[]}
 */
function expandDelegationDataToJobList (data) {
  if (data === null || data === undefined) return []
  if (Array.isArray(data)) {
    return data.flatMap((item) => expandDelegationDataToJobList(item))
  }
  if (typeof data !== 'object') return []

  if (Array.isArray(data.jobs) && data.jobs.length > 0) {
    const fromJobs = data.jobs.flatMap((j) => {
      if (looksLikeDelegatedDeletionCiJobPayload(j)) return [j]
      return expandDelegationDataToJobList(j)
    })
    if (fromJobs.length > 0) return fromJobs
  }

  const nestedBuilds = DELEGATION_NESTED_BUILD_KEYS
    .map((k) => data[k])
    .filter((b) => looksLikeDelegatedDeletionCiJobPayload(b))
  if (nestedBuilds.length > 0) return nestedBuilds

  if (looksLikeDelegatedDeletionCiJobPayload(data)) return [data]

  if (_.isPlainObject(data.data) && !Array.isArray(data.data)) {
    const inner = expandDelegationDataToJobList(data.data)
    if (inner.length > 0) return inner
  }

  return []
}

/**
 * @param {object|null|undefined} data - Response body `data` from DELETE
 * @returns {boolean}
 */
export function isDeletionDelegatedToPipeline (data) {
  if (data === null || data === undefined || typeof data !== 'object') return false
  if (!Array.isArray(data)) {
    if ((_.isBoolean(data.deletion_delegated) && data.deletion_delegated)
      || (_.isBoolean(data.deletionDelegated) && data.deletionDelegated)) return true
    if (_.isBoolean(data.pipeline_hook_triggered) && data.pipeline_hook_triggered) return true
    if (Array.isArray(data.jobs) && data.jobs.length > 0 && data.jobs.every(looksLikeDelegatedDeletionCiJobPayload)) return true
    if (looksLikeDelegatedDeletionCiJobPayload(data)) return true
  }

  const normalized = normalizeDelegationPayload(data)
  return expandDelegationDataToJobList(normalized).length > 0
}

/**
 * When `job_name` is absent, Concourse still sends `name` for resource-check builds (e.g. `"check"`).
 * Used **only** for alert copy — we do **not** build SPA `/jobs/…/builds/…` links from `name` (those URLs need a real job slug).
 * Purely numeric `name` values are build labels (e.g. `"19"`), not display names — ignore those here.
 *
 * @param {object} job
 * @returns {string}
 */
function delegatedDeletionFallbackSlugFromName (job) {
  const raw = typeof job?.name === 'string' ? job.name.trim() : ''
  if (!raw || /^\d+$/.test(raw)) return ''
  return raw
}

/** Slug for `builds` route only when BE sends a job (`job_name` / `job_canonical` / nested hook). Never `name`. */
function delegatedDeletionJobSlugForRoutes (job) {
  if (!job || typeof job !== 'object') return ''
  const explicit = job.job_name ?? job.job_canonical ?? job.pipeline_hook?.job_name
  return typeof explicit === 'string' && explicit.length > 0 ? explicit : ''
}

/** Display name in alerts: explicit job fields, else non-numeric `name` (resource-check builds — text only, no link). */
function delegatedDeletionJobDisplayName (job) {
  const explicit = job?.job_name ?? job?.job_canonical ?? job?.pipeline_hook?.job_name
  if (typeof explicit === 'string' && explicit.length > 0) return explicit
  return delegatedDeletionFallbackSlugFromName(job)
}

function pipelineCanonicalFromJob (job) {
  if (typeof job?.pipeline_name === 'string' && job.pipeline_name.length > 0) return job.pipeline_name
  if (typeof job?.pipeline_canonical === 'string' && job.pipeline_canonical.length > 0) return job.pipeline_canonical
  const nested = job?.pipeline_hook?.pipeline_name
  if (typeof nested === 'string' && nested.length > 0) return nested
  return ''
}

function resolveScopedRouteParams (routeParams, job) {
  return {
    orgCanonical: routeParams?.orgCanonical,
    projectCanonical: routeParams?.projectCanonical,
    envCanonical: routeParams?.envCanonical ?? job.environment_canonical ?? job.env_canonical,
    componentCanonical: routeParams?.componentCanonical ?? job.component_canonical ?? _.get(job, 'component.canonical'),
  }
}

/**
 * Extra query params so links encode **build id** + **pipeline id** alongside path params (`pipeline_name`, job slug).
 * Pages may ignore these; they document API context and match what the backend sends.
 *
 * @param {object} job
 * @returns {Record<string, string>}
 */
function delegatedDeletionRouteQuery (job) {
  const q = {}
  const buildId = job?.id ?? job?.build_id
  if (buildId !== undefined && buildId !== null && String(buildId).length > 0) {
    q.buildId = String(buildId)
  }
  const pipelineId = job?.pipeline_id
  if (pipelineId !== undefined && pipelineId !== null && String(pipelineId).length > 0) {
    q.pipeline_id = String(pipelineId)
  }
  return q
}

/**
 * SPA link to **job** build (`builds` route): pipeline canonical + **explicit** job slug + build id.
 * Does **not** link resource-check payloads that only provide `name` (e.g. `"check"`) without `job_name`.
 *
 * @param {object} job
 * @param {{ orgCanonical: string, projectCanonical: string, envCanonical?: string, componentCanonical?: string }|null|undefined} routeParams
 * @returns {string|null}
 */
export function resolveDelegatedDeletionBuildHref (job, routeParams) {
  if (!job || typeof job !== 'object') return null

  const pipelineCanonical = pipelineCanonicalFromJob(job)
  const buildId = job.id ?? job.build_id

  const { orgCanonical, projectCanonical, envCanonical, componentCanonical } = resolveScopedRouteParams(routeParams, job)
  const scopeOk = Boolean(orgCanonical && projectCanonical && envCanonical && componentCanonical && pipelineCanonical)

  const routeQuery = delegatedDeletionRouteQuery(job)
  const routeQueryOption = Object.keys(routeQuery).length > 0 ? { query: routeQuery } : {}

  const jobSlug = delegatedDeletionJobSlugForRoutes(job)
  if (!scopeOk || !jobSlug || buildId === undefined || buildId === null) return null

  try {
    return getRouter().resolve({
      name: 'builds',
      params: {
        orgCanonical,
        projectCanonical,
        envCanonical,
        componentCanonical,
        pipelineCanonical,
        jobCanonical: jobSlug,
        buildId: String(buildId),
      },
      ...routeQueryOption,
    }).href
  } catch {
    return null
  }
}

/**
 * SPA link for “pipeline overview” in delegated-deletion alerts:
 * – **Env component** scope: `/organizations/…/projects/…/environments/…/components/…/pipelines/:pipelineCanonical`
 *   (`envComponentPipeline`), using the pipeline canonical from the API payload.
 * – **Project / environment** deletes (no component in route params): org **Observability → Pipelines**
 *   (`pipelinesOverview`) as a fallback.
 *
 * @param {object|null|undefined} job - Build / hook payload (carries `pipeline_name` / `pipeline_canonical`).
 * @param {{ orgCanonical?: string, projectCanonical?: string, envCanonical?: string, componentCanonical?: string }|null|undefined} routeParams
 * @returns {string|null}
 */
export function resolveDelegatedDeletionPipelineOverviewHref (job, routeParams) {
  const pipelineCanonical = job && typeof job === 'object' ? pipelineCanonicalFromJob(job) : ''
  const { orgCanonical, projectCanonical, envCanonical, componentCanonical } = resolveScopedRouteParams(routeParams, job)

  const envComponentScopeOk = Boolean(
    orgCanonical && projectCanonical && envCanonical && componentCanonical && pipelineCanonical,
  )

  if (envComponentScopeOk) {
    try {
      return getRouter().resolve({
        name: 'envComponentPipeline',
        params: {
          orgCanonical,
          projectCanonical,
          envCanonical,
          componentCanonical,
          pipelineCanonical,
        },
      }).href
    } catch {
      return null
    }
  }

  if (!orgCanonical) return null
  try {
    return getRouter().resolve({
      name: 'pipelinesOverview',
      params: { orgCanonical },
    }).href
  } catch {
    return null
  }
}

/**
 * @param {object} job
 * @param {function(string, Object): string} t
 * @param {object|null} [routeParams]
 * @param {string} [localePrefix]
 * @returns {string}
 */
function formatSingleDelegatedDeletionJob (job, t, routeParams, localePrefix) {
  const buildHref = routeParams ? resolveDelegatedDeletionBuildHref(job, routeParams) : null
  const pipelineOverviewHref = routeParams ? resolveDelegatedDeletionPipelineOverviewHref(job, routeParams) : null
  const alertHref = pipelineOverviewHref ?? buildHref

  const jobName = delegatedDeletionJobDisplayName(job)
  const pipelineName = job?.pipeline_name ?? job?.pipeline_canonical ?? job?.pipeline_hook?.pipeline_name ?? ''
  const buildLabel = (_.isString(job?.name) && job.name)
    || (job?.id !== undefined && job?.id !== null ? `#${job.id}` : '—')
  const status = (_.isString(job?.status) && job.status) || '—'

  if (alertHref && jobName && pipelineName) {
    return t(`${localePrefix}.bodyWithLink`, {
      jobName,
      pipelineName,
      buildLabel,
      status,
      buildHref: alertHref,
      pipelineOverviewHref: alertHref,
    })
  }

  if (jobName && pipelineName) {
    return t(`${localePrefix}.bodyNoLink`, {
      jobName,
      pipelineName,
      buildLabel,
      status,
    })
  }

  return t(`${localePrefix}.generic`)
}

/**
 * @param {object|Array} data - Response `data` from DELETE: flat build, `{ jobs: [...] }`, job activity (`finished_build` / `next_build`), or an array of those.
 * @param {function(string, Object): string} t - `i18n.global.t`
 * @param {{
 *   orgCanonical: string,
 *   projectCanonical: string,
 *   envCanonical?: string,
 *   componentCanonical?: string,
 * }|null} [routeParams]
 * @param {{ localePrefix?: string }} [options]
 * @returns {string} HTML message for CyAlert (sanitized on render)
 */
export function getDelegatedDeletionAlertHtml (data, t, routeParams = null, { localePrefix = 'alerts.info.envComponent.deletionDelegated' } = {}) {
  const normalized = normalizeDelegationPayload(data)
  const jobs = expandDelegationDataToJobList(normalized)
  if (jobs.length === 0) {
    if (normalized !== null && normalized !== undefined && typeof normalized === 'object' && !Array.isArray(normalized)) {
      const formatted = formatSingleDelegatedDeletionJob(normalized, t, routeParams, localePrefix)
      if (formatted !== t(`${localePrefix}.generic`)) return formatted
    }
    if (data?.message && typeof data.message === 'string') return data.message
    return t(`${localePrefix}.generic`)
  }

  if (jobs.length === 1) {
    return formatSingleDelegatedDeletionJob(jobs[0], t, routeParams, localePrefix)
  }

  const intro = t(`${localePrefix}.multiScheduledIntro`)
  const blocks = jobs.map((job) => formatSingleDelegatedDeletionJob(job, t, routeParams, localePrefix))
  return `${intro}<div class="mt-2">${blocks.join('<br/><br/>')}</div>`
}
